/**
 * A rolling window of closed candles for one symbol and interval.
 *
 * Strategies that read bars rather than ticks need two things the rest of the app did not
 * provide together: enough history for a long indicator warm-up, and a callback the moment
 * a new bar finalises. The chart fetches history and `liveChartStream` publishes live bars,
 * but the AI path had neither, so it sampled the ticker on a timer instead — which cannot
 * produce a real open/high/low and has no notion of a bar closing.
 *
 * History is seeded from Binance's REST klines endpoint and kept current from the kline
 * websocket. `liveChartStream` reference counts its sockets per symbol and interval, so a
 * strategy watching 1m bars costs nothing extra when the chart happens to show the same
 * pair and interval, and does not disturb it when it shows something else.
 */

import { Candle } from './vidyaHeart';
import liveChartStream from './liveChartStream';

/** Binance caps a klines request at 1000 bars. */
const MAX_REQUEST = 1000;

export interface CandleFeedHandlers {
  /** Called once per newly closed bar, with the whole window, oldest first. */
  onClosedBar: (candles: Candle[]) => void;
  /** Called once when the seeded history has landed. */
  onReady?: (candles: Candle[]) => void;
  onError?: (error: unknown) => void;
}

function parseKline(entry: any[]): Candle | null {
  const candle: Candle = {
    time: Math.floor(Number(entry[0]) / 1000),
    open: parseFloat(entry[1]),
    high: parseFloat(entry[2]),
    low: parseFloat(entry[3]),
    close: parseFloat(entry[4])
  };

  const valid =
    Number.isFinite(candle.time) &&
    Number.isFinite(candle.open) &&
    Number.isFinite(candle.high) &&
    Number.isFinite(candle.low) &&
    Number.isFinite(candle.close);

  return valid ? candle : null;
}

/**
 * Subscribe to closed candles. Returns an unsubscribe function.
 *
 * `windowSize` is how much history to hold; it is also what is requested up front, so it
 * should cover the longest indicator warm-up the caller needs.
 */
export function subscribeCandles(
  symbol: string,
  interval: string,
  windowSize: number,
  handlers: CandleFeedHandlers
): () => void {
  let candles: Candle[] = [];
  let seeded = false;
  let disposed = false;
  // Bars that close while the seed request is still in flight, replayed once it lands so
  // the window has no hole in it.
  let buffered: Candle[] = [];

  const limit = Math.min(Math.max(windowSize, 2), MAX_REQUEST);

  const append = (candle: Candle): boolean => {
    const last = candles[candles.length - 1];

    if (last && candle.time < last.time) return false;
    if (last && candle.time === last.time) {
      // A correction to the bar we already hold rather than a new one.
      candles[candles.length - 1] = candle;
      return false;
    }

    candles.push(candle);
    if (candles.length > windowSize) {
      candles = candles.slice(candles.length - windowSize);
    }
    return true;
  };

  const seed = async () => {
    try {
      const response = await fetch(
        `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${interval}&limit=${limit}`
      );

      if (!response.ok) {
        throw new Error(`klines request failed for ${symbol} ${interval}: ${response.status}`);
      }

      const payload = await response.json();
      if (disposed) return;

      if (!Array.isArray(payload)) {
        throw new Error(`unexpected klines payload for ${symbol} ${interval}`);
      }

      const history = payload
        .map(parseKline)
        .filter((candle): candle is Candle => candle !== null);

      // The last entry is the bar still forming, which is not a close and must not be fed
      // to a strategy that acts on closes.
      history.pop();
      candles = history;
      seeded = true;

      let advanced = false;
      buffered.forEach(candle => {
        if (append(candle)) advanced = true;
      });
      buffered = [];

      handlers.onReady?.(candles);
      if (advanced) handlers.onClosedBar(candles);
    } catch (error) {
      if (!disposed) handlers.onError?.(error);
    }
  };

  const unsubscribeStream = liveChartStream.subscribe(symbol, interval, {
    onKline: kline => {
      if (disposed || !kline.isClosed) return;

      const candle: Candle = {
        time: kline.time,
        open: kline.open,
        high: kline.high,
        low: kline.low,
        close: kline.close
      };

      if (!seeded) {
        buffered.push(candle);
        return;
      }

      if (append(candle)) handlers.onClosedBar(candles);
    }
  });

  seed();

  return () => {
    disposed = true;
    buffered = [];
    unsubscribeStream();
  };
}
